import { MapPin, Phone, Mail, Calendar, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { BrokerageLocation } from "@/contracts/brokerage";

interface LocationCardProps {
  location: BrokerageLocation;
  distance?: number | null;
  onBookClick: (location: BrokerageLocation) => void;
}

export function LocationCard({ location, distance, onBookClick }: LocationCardProps) {
  const hasDirectBooking = !!location.booking_url;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-2">
            {/* Office name and location */}
            <div>
              <h4 className="font-semibold text-base">{location.name}</h4>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">
                  {location.city}, {location.state}
                  {location.address && ` • ${location.address}`}
                </span>
              </div>
            </div>

            {/* Distance badge */}
            {distance !== undefined && distance !== null && (
              <Badge variant="secondary" className="text-xs">
                {Math.round(distance)} miles away
              </Badge>
            )}

            {/* Virtual badge */}
            {location.is_virtual && (
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                Virtual Office
              </Badge>
            )}

            {/* Recruiter info */}
            {location.recruiter_name && (
              <div className="pt-2 space-y-1">
                <p className="text-sm font-medium">{location.recruiter_name}</p>
                {location.recruiter_email && (
                  <a
                    href={`mailto:${location.recruiter_email}`}
                    className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    {location.recruiter_email}
                  </a>
                )}
                {location.recruiter_phone && (
                  <a
                    href={`tel:${location.recruiter_phone}`}
                    className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {location.recruiter_phone}
                  </a>
                )}
              </div>
            )}

            {/* Location pitch */}
            {location.location_pitch && (
              <p className="text-sm text-muted-foreground line-clamp-2 pt-1">
                {location.location_pitch}
              </p>
            )}
          </div>

          {/* Book button */}
          <div className="flex-shrink-0">
            <Button 
              onClick={() => onBookClick(location)}
              className="gap-2"
              size="sm"
            >
              {hasDirectBooking ? (
                <>
                  <Calendar className="h-4 w-4" />
                  Book Call
                  <ExternalLink className="h-3 w-3" />
                </>
              ) : (
                <>
                  <Calendar className="h-4 w-4" />
                  Request Info
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
